<script setup lang="ts">
import AuthFlow from '@/components/shared/AuthFlow.vue';
import SelectUserOverlay from '@/overlays/SelectUserOverlay.vue';
import DateTimeSelectOverlay from '@/views/booking/DateTimeSelectOverlay.vue';
import { onMounted, ref } from 'vue';
import { useBookingResourcesStore } from '@/stores/bookingResources';
import { IProfile } from '@/interfaces';
import SuccessOverlay from '@/overlays/SuccessOverlay.vue';

const bookingResourcesStore = useBookingResourcesStore();

const emit = defineEmits(['close', 'success']);

interface Props {
  employeeOnly?: boolean;
  resource: any;
}

const props = withDefaults(defineProps<Props>(), {
  employeeOnly: false,
});

const showAuthFlow = ref<boolean>(false);
const showDateTimeSelectOverlay = ref<boolean>(false);
const showSelectUserOverlay = ref<boolean>(false);
const selectedProfiles = ref<IProfile[]>();
const showSuccessOverlay = ref<boolean>(false);

const closeFlow = () => {
  showAuthFlow.value = false;
  showDateTimeSelectOverlay.value = false;
  emit('close');
};

const onAuthorized = () => {
  showAuthFlow.value = false;
  showDateTimeSelectOverlay.value = true;
};

const profilesSelected = (profiles: IProfile[]) => {
  selectedProfiles.value = profiles;
};

const onConfirmBooking = async () => {
  await bookingResourcesStore.setNewBooking({
    participants: selectedProfiles.value,
  });

  await bookingResourcesStore.createNewBooking();
  await bookingResourcesStore.getBookingResources();

  showSuccessOverlay.value = true;
};

onMounted(() => {
  if (['PASSWORD', 'EMPLOYEE_ONLY'].includes(props.resource.authentication)) {
    showAuthFlow.value = true;
  } else {
    showDateTimeSelectOverlay.value = true;
  }
});
</script>

<template>
  <AuthFlow
    v-if="showAuthFlow"
    :shared-departments="resource.departments"
    :title-success="$t('booking.completed')"
    :title="$t('booking.bookResource')"
    :title-bold="resource.title"
    :employee-only="props.resource.authentication === 'EMPLOYEE_ONLY'"
    @close="closeFlow"
    @success="onAuthorized"
  />

  <DateTimeSelectOverlay
    v-if="showDateTimeSelectOverlay"
    :resource="props.resource"
    @close="closeFlow"
    @next="showSelectUserOverlay = true"
  />

  <SelectUserOverlay
    v-if="showSelectUserOverlay"
    :multi-select="true"
    :square-tiles="true"
    :participants="[]"
    :shared-departments="props.resource.departments"
    :title="$t('booking.selectParticipantsTitle')"
    :sub-title="$t('booking.selecttParticipantsDescription')"
    textAlignment="center"
    :confirm-button-text="$t('booking.confirmBooking')"
    @profiles-selected="profilesSelected"
    @close="showSelectUserOverlay = false"
    @confirm="onConfirmBooking"
  />

  <SuccessOverlay
    :title="$t('booking.bookingCreated')"
    v-if="showSuccessOverlay"
  />
</template>
